import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  Home,
  CheckCircle,
  Star,
  Package,
  Lightbulb,
  Palette,
  Phone
} from 'lucide-react';
import { Link } from 'react-router-dom';

const FittedBedroomFurniturePage = () => {
  const bedroomFurnitureTypes = [
    {
      name: 'Fitted Dressing Tables',
      subtitle: 'Elegant Beauty Station',
      description: 'Bespoke dressing tables with integrated storage, mirrors, and lighting. Perfect for creating your personal beauty station within fitted bedroom furniture.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png',
      features: [
        'Built-in LED lighting',
        'Hidden cable management',
        'Drawers with soft-close',
        'Mirror options available',
        'Power socket integration',
        'All finish colors'
      ],
      priceRange: 'From £800',
      color: 'text-pink-600'
    },
    {
      name: 'Fitted Bedside Tables',
      subtitle: 'Seamless Integration',
      description: 'Floating or floor-standing bedside units that integrate perfectly with your fitted wardrobes for a cohesive bedroom design.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/3ad24081315c82e8441c5431ac32334c66635adc8d92beaa733c307ff91165fe.png',
      features: [
        'Floating or standing options',
        'USB charging points',
        'Concealed lighting',
        'Soft-close drawers',
        'Wireless charging zones',
        'Cable management'
      ],
      priceRange: 'From £450',
      color: 'text-[#8CB89F]'
    },
    {
      name: 'Fitted Chest of Drawers',
      subtitle: 'Maximum Storage',
      description: 'Floor-to-ceiling or under-window chest of drawers with premium soft-close mechanisms and internal organization systems.',
      image: 'https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/79f2e9a3e46d63c12d78f97eafcab5112c7aa1e3bfaeb76a3fc34cf30ef86b44.png',
      features: [
        'Soft-close drawer runners',
        'Internal dividers',
        'Full extension drawers',
        'Anti-tilt mechanisms',
        'Premium handles',
        'Bespoke configurations'
      ],
      priceRange: 'From £950',
      color: 'text-green-600'
    }
  ];

  const bedroomBenefits = [
    {
      icon: Package,
      title: 'Coordinated Design',
      description: 'All bedroom furniture matches perfectly for a cohesive, designer look'
    },
    {
      icon: Lightbulb,
      title: 'Integrated Technology',
      description: 'Built-in lighting, charging points, and smart storage solutions'
    },
    {
      icon: Palette,
      title: 'Endless Finishes',
      description: 'Match your wardrobes exactly with the same finish options'
    },
    {
      icon: Star,
      title: 'Space Optimization',
      description: 'Every piece designed to maximize your bedroom\'s potential'
    }
  ];

  const furnitureOptions = [
    {
      type: 'Dressing Tables',
      options: ['Corner units', 'Linear designs', 'Island styles', 'Floating options'],
      features: ['LED lighting', 'Power sockets', 'Hidden storage', 'Mirror integration']
    },
    {
      type: 'Bedside Tables',
      options: ['Floating units', 'Floor standing', 'Built-in designs', 'Corner solutions'],
      features: ['USB charging', 'Wireless charging', 'LED strips', 'Hidden drawers']
    },
    {
      type: 'Chest of Drawers',
      options: ['Tall units', 'Wide formats', 'Under-window', 'Corner units'],
      features: ['Soft-close runners', 'Internal dividers', 'Anti-tilt safety', 'Full extension']
    },
    {
      type: 'Additional Items',
      options: ['TV units', 'Reading chairs', 'Ottoman storage', 'Built-in seating'],
      features: ['Cable management', 'Integrated power', 'Hidden storage', 'Custom sizing']
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-rose-50 to-pink-50 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="lg:flex items-center gap-12">
            <div className="lg:w-1/2 mb-8 lg:mb-0">
              <div className="flex items-center gap-2 mb-4">
                <Package className="h-6 w-6" style={{color: '#8CB89F'}} />
                <Badge className="text-white" style={{backgroundColor: '#8CB89F'}}>Complete Bedroom</Badge>
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Fitted Bedroom Furniture
                <span className="block" style={{color: '#8CB89F'}}>Complete Bedroom Solutions</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                <strong>Aston Lily Joinery</strong> creates complete fitted bedroom solutions. From dressing tables to chest of drawers, every piece perfectly coordinated for a designer bedroom.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="text-white hover:opacity-90 px-8 py-4 text-lg" style={{backgroundColor: '#8CB89F'}}>
                    <Calculator className="mr-2 h-5 w-5" />
                    Get Bedroom Quote
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="px-8 py-4 text-lg border-2"
                    style={{borderColor: '#8CB89F', color: '#8CB89F'}}
                  >
                    <Phone className="mr-2 h-5 w-5" />
                    Bedroom Design Service
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:w-1/2">
              <img 
                src="https://static.prod-images.emergentagent.com/jobs/b91ea611-2364-4d55-8415-0e2be2797319/images/587d19c8b4573aed50340d8e079badc98afc137cb37f38b7d447fc336fc69c5d.png"
                alt="Fitted Bedroom Furniture"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16" style={{backgroundColor: '#8CB89F10'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Fitted Bedroom Furniture?</h2>
            <p className="text-xl text-gray-600">
              Complete bedroom coordination with smart storage solutions
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {bedroomBenefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-8 text-center">
                  <benefit.icon className="h-12 w-12 mx-auto mb-4" style={{color: '#8CB89F'}} />
                  <h3 className="text-xl font-semibold mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Furniture Types */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Bedroom Furniture Types</h2>
            <p className="text-xl text-gray-600">
              Complete your bedroom with our fitted furniture range
            </p>
          </div>
          
          <div className="space-y-20">
            {bedroomFurnitureTypes.map((furniture, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <img 
                    src={furniture.image} 
                    alt={furniture.name}
                    className="w-full h-96 object-cover rounded-2xl shadow-xl"
                  />
                </div>
                <div className="lg:w-1/2">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge variant="outline" className={furniture.color}>{furniture.subtitle}</Badge>
                  </div>
                  
                  <h3 className="text-4xl font-bold text-gray-900 mb-4">{furniture.name}</h3>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{furniture.description}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    {furniture.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <Home className="h-5 w-5 text-[#8CB89F]" />
                      <span className="font-semibold text-lg">{furniture.priceRange}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Furniture Options */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Furniture Options</h2>
            <p className="text-xl text-gray-600">
              Explore the possibilities for your fitted bedroom
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {furnitureOptions.map((option, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-xl">{option.type}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Style Options:</h4>
                    <div className="space-y-1">
                      {option.options.map((opt, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="h-3 w-3" style={{color: '#8CB89F'}} />
                          <span className="text-sm text-gray-600">{opt}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Key Features:</h4>
                    <div className="space-y-1">
                      {option.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Star className="h-3 w-3 text-yellow-500" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Create Your Perfect Bedroom</h2>
          <p className="text-xl mb-8 opacity-90">
            Get a comprehensive bedroom design and quote including wardrobes, dressing tables, and all fitted furniture from Aston Lily Joinery.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Complete Bedroom Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
              >
                <Phone className="mr-2 h-5 w-5" />
                Bedroom Design Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FittedBedroomFurniturePage;